import { Image as ImageIcon } from 'lucide-react';
import type { PostListItem } from '../../../shared/types';
import { platformName } from '../../../shared/platforms';
import { platformPostStatusLabel } from '../../../shared/utils';
import { Badge, PlatformIcon, Tooltip } from '../ui';
import { PLATFORM_POST_STATUS_TONE, POST_STATUS_TONE, formatRelative, formatWhen } from '../../lib/format';
import { cn } from '../../lib/utils';
import { PostStatusBadge } from './PostStatusBadge';

const TONE_DOT: Record<'neutral' | 'info' | 'success' | 'warning' | 'danger', string> = {
  neutral: 'bg-subtle',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

function PlatformStatusPip({
  entry,
  timezone,
}: {
  entry: PostListItem['platforms'][number];
  timezone: string;
}) {
  const tone = PLATFORM_POST_STATUS_TONE[entry.status];
  const when = entry.publishedAt ?? entry.scheduledAt;
  const tooltip = (
    <span>
      {platformName(entry.platform)} · {platformPostStatusLabel(entry.status)}
      {when ? ` · ${formatWhen(when, timezone)}` : ''}
      {entry.simulated ? ' · simulated' : ''}
    </span>
  );

  return (
    <Tooltip content={tooltip} side="top">
      <span className="relative inline-flex shrink-0">
        <PlatformIcon platform={entry.platform} size="sm" variant="soft" />
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 size-2 rounded-full ring-2 ring-surface',
            TONE_DOT[tone],
          )}
          aria-hidden
        />
      </span>
    </Tooltip>
  );
}

export interface PostListRowProps {
  post: PostListItem;
  timezone: string;
  onOpen: (postId: string) => void;
}

/** One row in the posts list: title, per-platform status pips, excerpt. Owned by WEB-3. */
export function PostListRow({ post, timezone, onOpen }: PostListRowProps) {
  const title = post.title?.trim() || post.excerpt || 'Untitled post';
  const overallTone = POST_STATUS_TONE[post.status];

  return (
    <button
      type="button"
      onClick={() => onOpen(post.id)}
      className={cn(
        'flex w-full items-start gap-4 border-b border-border px-4 py-4 text-left last:border-b-0',
        'transition-colors hover:bg-elevated/40 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring',
      )}
    >
      <span
        className={cn('mt-1.5 size-2 shrink-0 rounded-full', TONE_DOT[overallTone])}
        aria-hidden
      />
      <span className="min-w-0 flex-1 space-y-1.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-fg">{title}</span>
          <PostStatusBadge status={post.status} />
          {post.mediaCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-subtle">
              <ImageIcon className="size-3" aria-hidden />
              {post.mediaCount}
            </span>
          )}
        </span>
        {post.excerpt && (
          <span className="pap-clamp-1 block text-sm text-muted">{post.excerpt}</span>
        )}
        <span className="flex flex-wrap items-center gap-2 pt-0.5">
          {post.platforms.length ? (
            post.platforms.map((entry) => (
              <PlatformStatusPip key={entry.id} entry={entry} timezone={timezone} />
            ))
          ) : (
            <Badge tone="neutral" size="sm">
              No platforms yet
            </Badge>
          )}
        </span>
      </span>
      <span className="shrink-0 pt-0.5 text-right text-xs text-subtle">
        {formatRelative(post.updatedAt)}
      </span>
    </button>
  );
}
